import { z } from 'zod';
import { booleanQueryParam } from './query-params.js';
/**
 * The portal's view of a captured article.
 *
 * Deliberately separate from the sync payloads in sync.ts. Those describe what
 * a device sends and are shaped by what the device knows; this describes what a
 * person looking at a list needs, and carries resolved names — the supplier's
 * label, the capturer's name — that a device would look up for itself.
 */
/** What the list can be sorted by. Every one of these is indexed. */
export const ITEM_SORT_FIELDS = [
    'clientCreatedAt',
    'serverCreatedAt',
    'shortCode',
    'updatedAt',
];
export const itemSortSchema = z.enum(ITEM_SORT_FIELDS);
export const itemListQuerySchema = z.object({
    /** Free text across the short code, the barcode and the jsonb attributes. */
    search: z.string().max(200).optional(),
    eventId: z.uuid().optional(),
    supplierItemId: z.uuid().optional(),
    createdByUserId: z.uuid().optional(),
    capturedFrom: z.iso.datetime({ offset: true }).optional(),
    capturedTo: z.iso.datetime({ offset: true }).optional(),
    /** Narrow to the ones flagged for follow-up. */
    needsReviewOnly: booleanQueryParam.default(false),
    /**
     * Show deleted records too.
     *
     * Deletion is a tombstone, never a removal — devices have to learn that an
     * article went away. Without this the tombstones would be unreachable, and a
     * deletion by mistake would be unrecoverable through the portal.
     */
    includeDeleted: booleanQueryParam.default(false),
    sort: itemSortSchema.default('clientCreatedAt'),
    direction: z.enum(['asc', 'desc']).default('desc'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const itemPhotoViewSchema = z.object({
    id: z.uuid(),
    attributeKey: z.string(),
    contentType: z.string(),
    byteSize: z.number().int().nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    sortOrder: z.number().int(),
    uploadStatus: z.enum(['pending', 'uploading', 'uploaded', 'failed']),
    capturedAt: z.iso.datetime({ offset: true }).nullable(),
    /**
     * A time-limited link straight to the object store.
     *
     * Null while the upload has not completed — there is nothing to look at yet,
     * and handing out a URL for an object that does not exist would produce a
     * broken image with no explanation.
     */
    url: z.string().nullable(),
});
export const itemSummarySchema = z.object({
    id: z.uuid(),
    shortCode: z.string().nullable(),
    barcode: z.string().nullable(),
    eventId: z.uuid(),
    eventCode: z.string(),
    eventName: z.string(),
    supplierItemId: z.uuid().nullable(),
    supplierLabel: z.string().nullable(),
    createdByUserId: z.uuid(),
    createdByName: z.string().nullable(),
    clientCreatedAt: z.iso.datetime({ offset: true }),
    serverCreatedAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
    schemaVersion: z.number().int(),
    needsReview: z.boolean(),
    revision: z.number().int(),
    deletedAt: z.iso.datetime({ offset: true }).nullable(),
    photoCount: z.number().int(),
    /** Only the fields marked showInList, resolved to display strings. */
    listValues: z.record(z.string(), z.string()),
});
export const itemDetailSchema = itemSummarySchema.extend({
    attributes: z.record(z.string(), z.unknown()),
    photos: z.array(itemPhotoViewSchema),
});
export const itemListResponseSchema = z.object({
    items: z.array(itemSummarySchema),
    total: z.number().int(),
});
export const itemCreateSchema = z.object({
    eventId: z.uuid(),
    barcode: z.string().max(200).nullable().default(null),
    supplierItemId: z.uuid().nullable().default(null),
    attributes: z.record(z.string(), z.unknown()).default({}),
    /**
     * When the article was captured, as opposed to when the row was written.
     *
     * Left open for a manual entry because somebody typing up a stack of notes a
     * day later should be able to say when the article was actually seen —
     * otherwise every filter by capture date puts it on the wrong day.
     */
    clientCreatedAt: z.iso.datetime({ offset: true }).nullable().default(null),
});
export const itemUpdateSchema = z.object({
    barcode: z.string().max(200).nullable().optional(),
    supplierItemId: z.uuid().nullable().optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
    needsReview: z.boolean().optional(),
    /**
     * The revision the editor started from.
     *
     * Required, not optional. A device syncing the same article a second apart is
     * ordinary at a fair, and an update without a base revision would overwrite
     * whatever arrived in between without anyone noticing.
     */
    baseRevision: z.number().int().min(1),
});
//# sourceMappingURL=item-api.js.map